import { useState } from "react";
import { useAdminIntegrations } from "@/hooks/queries/admin/useAdminIntegrations";

export default function AdminIntegrations(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">(
    "all"
  );
  const { data, isLoading, error } = useAdminIntegrations(statusFilter);

  if (isLoading)
    return (
      <div className="text-sm text-text-secondary dark:text-brand-100/70">
        Loading…
      </div>
    );
  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400">
        Couldn't load integrations: {error.message}
      </div>
    );
  }

  const integrations = data?.integrations ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary dark:text-text-primary">
          Integrations
        </h1>
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as "all" | "active" | "inactive")
          }
          className="rounded border border-brand-200 bg-white px-2 py-1 text-sm dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary"
        >
          <option value="all">All</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-brand-100 bg-white shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
        <table className="w-full text-sm">
          <thead className="bg-surface-canvas text-left text-text-secondary dark:bg-white/[0.03] dark:text-brand-100/60">
            <tr>
              <th className="px-4 py-2 font-medium">Tenant</th>
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Last synced</th>
              <th className="px-4 py-2 font-medium">30-day counts</th>
              <th className="px-4 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {integrations.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-accent-primary dark:text-brand-100/40"
                >
                  No integrations.
                </td>
              </tr>
            ) : (
              integrations.map((i) => (
                <tr
                  key={i.id}
                  className="border-t border-brand-100 dark:border-white/[0.04]"
                >
                  <td className="px-4 py-2 text-text-primary dark:text-text-primary">
                    {i.tenant_name}{" "}
                    <span className="text-accent-primary dark:text-brand-100/50">
                      ({i.tenant_slug})
                    </span>
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {i.type}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {i.name}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {i.last_synced_at
                      ? new Date(i.last_synced_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {i.sync_counts_30d.success}✓ {i.sync_counts_30d.partial}~{" "}
                    {i.sync_counts_30d.failed}✗
                  </td>
                  <td className="px-4 py-2">
                    {i.is_active ? (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-brand-positive/15 dark:text-[var(--p-green-350)]">
                        active
                      </span>
                    ) : (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-white/[0.06] dark:text-brand-100/60">
                        inactive
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
